import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { OrderContext } from '../context/OrderContext';
import { useLocation, useNavigate } from 'react-router-dom';
import userService from '../services/userService';
import {
  FiArrowLeft,
  FiBox,
  FiCheck,
  FiClipboard,
  FiPlus,
  FiPrinter,
  FiSearch,
  FiUser,
  FiX,
} from 'react-icons/fi';
import '../css/Dashboard.css';
import '../css/AdminPage.css';
//import { jsPDF } from 'jspdf';
import { orderToPdf } from "../services/orderService";

import Pagination from './Pagination';

const COUNTRY_OPTIONS = [
  { value: '', label: '-' },
  { value: 'IT', label: 'Italia' },
  { value: 'AT', label: 'Austria' },
  { value: 'DE', label: 'Germania' },
  { value: 'ES', label: 'Spagna' },
  { value: 'FR', label: 'Francia' },
  { value: 'OTHER', label: 'Altro' },
];

const LANGUAGE_OPTIONS = [
  { value: '', label: 'Auto da paese' },
  { value: 'it', label: 'Italiano' },
  { value: 'en', label: 'Inglese' },
  { value: 'de', label: 'Tedesco' },
  { value: 'es', label: 'Spagnolo' },
  { value: 'fr', label: 'Francese' },
];

const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const {
    orders,
    markOrderAsReceived,
    rejectOrder,
    approveOrderEditRequest,
    rejectOrderEditRequest,
  } = useContext(OrderContext);
  const [users, setUsers] = useState([]);
  const [currentUsers, setCurrentUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [sortedUsers, setSortedUsers] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [userOrders, setUserOrders] = useState([]);
  const [newRole, setNewRole] = useState('');
  const [newDiscount, setNewDiscount] = useState(0);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCountry, setNewCountry] = useState('');
  const [newPreferredLanguage, setNewPreferredLanguage] = useState('');
  const [newQuoteEnabled, setNewQuoteEnabled] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(20); // Numero di utenti per pagina
  const [loading, setLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState(''); // Filtro per ruolo
  const [searchQuery, setSearchQuery] = useState(''); // Ricerca per nome o email
  const [actionFilter, setActionFilter] = useState('all'); // Filtro operativo rapido
  const [sortField, setSortField] = useState('name'); // Campo di default per l'ordinamento
  const [sortOrder, setSortOrder] = useState('asc'); // Ordine di default (ascendente)
  const [versionInfo, setVersionInfo] = useState(null);
  const [versionError, setVersionError] = useState(null);
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  // Redesign 2026-10 (solo aspetto): scheda cliente laterale, messaggi e conferme nella pagina
  // al posto di alert / prompt / confirm del browser. Stessi controlli e stesse chiamate.
  const [drawer, setDrawer] = useState(null); // { id, tab: 'profile' | 'orders' }
  const [notice, setNotice] = useState(null); // { text, kind: 'ok' | 'err' }
  const [noteBox, setNoteBox] = useState(null); // { action, orderId, text }
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showFeedbackTab, setShowFeedbackTab] = useState(false);

  const notify = (text, kind = 'ok') => setNotice({ text, kind, at: Date.now() });
  // Esito di "Nuovo utente" (CreateUser.js): prima era un alert prima di tornare qui.
  const location = useLocation();
  const navigateTo = useNavigate();
  useEffect(() => {
    const incoming = location.state?.notice;
    if (!incoming) return;
    setNotice({ ...incoming, at: Date.now() });
    navigateTo(location.pathname, { replace: true, state: null });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  useEffect(() => {
    if (!notice || notice.sticky) return undefined;
    const timer = setTimeout(() => setNotice(null), 3200);
    return () => clearTimeout(timer);
  }, [notice]);

  const parseMoneyValue = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value !== 'string') return 0;
    const normalized = value.replace(/\s/g, '').replace(',', '.').replace(/[^0-9.-]/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const orderAmount = (order) => {
    if (order?.total != null) {
      const parsedTotal = parseMoneyValue(order.total);
      if (parsedTotal > 0) return parsedTotal;
    }
    if (!Array.isArray(order?.items)) return 0;
    return order.items.reduce((sum, item) => {
      const unitPrice = parseMoneyValue(item?.unitPrice);
      const quantity = Number(item?.quantity) || 0;
      const discount = Number(item?.discount) || 0;
      return sum + (unitPrice * quantity * (1 - discount / 100));
    }, 0);
  };

  useEffect(() => {
    if (selectedUser) {
      setNewRole(selectedUser.role || '');
      setNewCompanyName(selectedUser.companyName || '');
      setNewDiscount(selectedUser.discount || 0);
      setNewCountry((selectedUser.country || '').toUpperCase());
      setNewPreferredLanguage(selectedUser.preferredLanguage || '');
      setNewQuoteEnabled(!!selectedUser.quoteEnabled);
      setNewPhoneNumber(selectedUser.phoneNumber || '');
      setNewAddress(selectedUser.address || '');
    }
  }, [selectedUser]);
  const navigate = useNavigate();

  const authToken = (user && user.token) || localStorage.getItem('token');

  useEffect(() => {
    const fetchUsers = async (token) => {
      setLoading(true);
      try {
        const usersData = await userService.getAllUsers(token);
        setUsers(usersData);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user && user.role === 'admin' && authToken) {
      console.log("fetchUsers:", user);
      fetchUsers(authToken);
    }
  }, [user, authToken]);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const res = await fetch('/api/version');
        if (!res.ok) throw new Error('version request failed');
        const data = await res.json();
        setVersionInfo(data);
      } catch (err) {
        console.error('Failed to fetch version:', err);
        setVersionError('Versione non disponibile');
      }
    };

    fetchVersion();
  }, []);

  useEffect(() => {
    const fetchFeedback = async () => {
      if (!authToken || user?.role !== 'admin') return;
      setFeedbackLoading(true);
      try {
        const list = await userService.getFeedbackList(authToken, feedbackStatusFilter);
        setFeedbackItems(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Failed to fetch feedback list:', err);
      } finally {
        setFeedbackLoading(false);
      }
    };
    fetchFeedback();
  }, [authToken, user, feedbackStatusFilter]);

  const feedbackStatusLabel = (status) => {
    if (status === 'new') return 'Nuovo';
    if (status === 'in_review') return 'In lavorazione';
    if (status === 'closed') return 'Chiuso';
    return status || '-';
  };

  const feedbackTypeLabel = (type) => {
    if (type === 'issue') return 'Problema';
    if (type === 'suggestion') return 'Suggerimento';
    return type || '-';
  };

  const handleFeedbackStatusChange = async (feedbackId, status) => {
    if (!feedbackId || !status || !authToken) return;
    try {
      const updated = await userService.updateFeedbackStatus(feedbackId, status, authToken);
      setFeedbackItems((prev) => prev.map((item) => (item._id === feedbackId ? updated : item)));
    } catch (err) {
      console.error('Failed to update feedback status:', err);
      notify('Aggiornamento stato feedback non riuscito.', 'err');
    }
  };

  useEffect(() => {
    // Funzione per filtrare gli utenti in base al ruolo e alla ricerca
    setFilteredUsers(users.filter((user) =>
      {
        const roleMatch = roleFilter ? user.role === roleFilter : true;
        const searchMatch = searchQuery
          ? user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.email.toLowerCase().includes(searchQuery.toLowerCase())
          : true;

        const userOrders = orders.filter((order) => order.userId === user._id);
        const preparatoCount = userOrders.filter((order) => order.status === 'preparato').length;
        const inviatoCount = userOrders.filter((order) => order.status === 'inviato' || order.status === 'edit_requested').length;
        const needsDiscount = !user.discount || Number(user.discount) === 0;

        let actionMatch = true;
        if (actionFilter === 'needs-discount') actionMatch = needsDiscount;
        if (actionFilter === 'to-prepare') actionMatch = preparatoCount > 0;
        if (actionFilter === 'sent-awaiting') actionMatch = inviatoCount > 0;
        if (actionFilter === 'online') actionMatch = !!user.isOnline;

        return roleMatch && searchMatch && actionMatch;
      }
    ));
  }, [users, roleFilter, searchQuery, orders, actionFilter])

  useEffect(() => {
    // Ordina gli utenti
    setSortedUsers([...filteredUsers].sort((a, b) => {
      const getSortValue = (item) => {
        if (sortField === 'createdAt' || sortField === 'lastAccess') {
          const ts = new Date(item?.[sortField]).getTime();
          return Number.isFinite(ts) ? ts : 0;
        }
        return String(item?.[sortField] || '').toLowerCase();
      };

      const fieldA = getSortValue(a);
      const fieldB = getSortValue(b);

      if (fieldA < fieldB) return sortOrder === 'asc' ? -1 : 1;
      if (fieldA > fieldB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    }));
  }, [filteredUsers, sortField, sortOrder])

  // Paginazione
  useEffect(() => {
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    setCurrentUsers(sortedUsers.slice(indexOfFirstUser, indexOfLastUser));
  }, [currentPage, sortedUsers, usersPerPage]);

  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
    setCurrentPage(1); // Resetta alla prima pagina dopo aver cambiato il filtro
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Resetta alla prima pagina dopo aver effettuato una ricerca
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Se il campo Ã¨ lo stesso, alterna l'ordine
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Se il campo Ã¨ diverso, ordina ascendente per default
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Resetta alla prima pagina dopo aver cambiato l'ordinamento
  };

  const handleBack = () => {
    setSelectedUser(null);
    setSelectedOrder(null);
    setUserOrders([]);
    setDrawer(null);
    setNoteBox(null);
    setConfirmDelete(false);
  };

  const handleViewOrderDetails = (orderId) => {
    const order = userOrders.find((order) => order._id === orderId);
    if (order) {
      setSelectedOrder(order);
    }
  };

  const handleManageOrders = (userId) => {
    const userSpecificOrders = orders.filter(
      (order) =>
        order.userId === userId &&
        (order.status === 'inviato' || order.status === 'ricevuto' || order.status === 'edit_requested')
    );
    setUserOrders(userSpecificOrders);
    setSelectedOrder(null);
  };

  // Apre la scheda laterale del cliente: profilo (come "Manage User") e ordini (come "Manage Order").
  const openUserDrawer = (target, tab) => {
    setSelectedUser(target);
    handleManageOrders(target._id);
    setNoteBox(null);
    setConfirmDelete(false);
    setDrawer({ id: target._id, tab });
  };

  const handleMarkAsReceived = async (orderId) => {
    const orderToUpdate = userOrders.find((order) => order._id === orderId);
    if (!orderToUpdate || !orderToUpdate._id) {
      console.error('Order ID is invalid or undefined');
      return;
    }

    try {
      await markOrderAsReceived(orderId);
      setUserOrders(
        userOrders.map((order) =>
          order._id === orderId ? { ...order, status: 'ricevuto' } : order
        )
      );
      notify('Ordine contrassegnato come ricevuto.');
      handleBack();
    } catch (error) {
      console.error('Failed to mark order as received:', error);
    }
  };

  const exportOrderToPDF = (order) => {
    /*const doc = new jsPDF();
    const companyInfo = [
      'Nome Azienda: GOOOLED',
      'Indirizzo: Via Torricelli 26',
      'Telefono: +39 3356059956',
      'Email: info@goooled.com',
      'Sito Web: www.gooooled.com',
      '',
    ];

    let startY = 10;
    companyInfo.forEach((line) => {
      doc.text(line, 10, startY);
      startY += 10;
    });

    const orderDetails = [
      `Numero d'Ordine: ${order.orderNumber}`,
      `Data di Creazione: ${new Date(order.date).toLocaleDateString()}`,
      `Riferimento: ${order.reference}`,
      `Utente: ${users.find((u) => u._id === order.userId)?.email || 'N/A'
      }`,
      '',
    ];

    orderDetails.forEach((line) => {
      doc.text(line, 10, startY);
      startY += 10;
    });

    const orderHeader = [
      '#',
      'Codice',
      'Descrizione',
      'Listino',
      'Sc%',
      'Prezzo Netto',
      'QtÃ ',
      'Prezzo Totale',
      'Note',
    ];
    const orderData = order.items.map((item, index) => [
      index + 1,
      item.code,
      item.description,
      `â‚¬${item.unitPrice.toFixed(2)}`,
      `${item.discount}%`,
      `â‚¬${(item.unitPrice * (1 - item.discount / 100)).toFixed(2)}`,
      item.quantity,
      `â‚¬${(
        item.unitPrice *
        item.quantity *
        (1 - item.discount / 100)
      ).toFixed(2)}`,
      item.notes || '',
    ]);

    doc.autoTable({
      head: [orderHeader],
      body: orderData,
      startY,
      styles: { cellPadding: 2, fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 20 },
        2: { cellWidth: 60 },
        3: { cellWidth: 20 },
        4: { cellWidth: 10 },
        5: { cellWidth: 20 },
        6: { cellWidth: 10 },
        7: { cellWidth: 20 },
        8: { cellWidth: 30 },
      },
      margin: { top: startY },
    });

    const totalAmount = order.items.reduce(
      (total, item) =>
        total +
        item.unitPrice * item.quantity * (1 - item.discount / 100),
      0
    );

    doc.text(
      `Totale Importo: â‚¬${totalAmount.toFixed(2)}`,
      10,
      doc.previousAutoTable.finalY + 10
    );

    doc.save(`ordine_${order.orderNumber}.pdf`);*/
    const orderOwner = users.find((u) => String(u._id) === String(order.userId));
    orderToPdf(order, orderOwner || user);
  };

  // Il motivo / la nota si scrivono nel riquadro della pagina (prima: window.prompt).
  const handleRejectOrder = async (orderId, reason) => {
    if (reason == null) return false;
    const cleanReason = String(reason).trim();
    if (!cleanReason) {
      notify('Inserisci un motivo di rifiuto.', 'err');
      return false;
    }
    try {
      const updated = await rejectOrder(orderId, cleanReason);
      setUserOrders((prev) => prev.map((o) => (o._id === orderId ? updated : o)));
      setSelectedOrder((prev) => (prev && prev._id === orderId ? updated : prev));
      notify('Ordine rifiutato.');
    } catch (error) {
      console.error('Failed to reject order:', error);
      notify('Errore durante il rifiuto ordine.', 'err');
    }
    return true;
  };

  const handleApproveEditRequest = async (orderId, noteText) => {
    const note = noteText || '';
    try {
      const updated = await approveOrderEditRequest(orderId, note);
      setUserOrders((prev) => prev.map((o) => (o._id === orderId ? updated : o)));
      setSelectedOrder((prev) => (prev && prev._id === orderId ? updated : prev));
      notify('Richiesta modifica approvata. Ordine riaperto al cliente.');
    } catch (error) {
      console.error('Failed to approve edit request:', error);
      notify('Errore durante l\'approvazione richiesta modifica.', 'err');
    }
    return true;
  };

  const handleRejectEditRequest = async (orderId, noteText) => {
    const note = noteText || '';
    try {
      const updated = await rejectOrderEditRequest(orderId, note);
      setUserOrders((prev) => prev.map((o) => (o._id === orderId ? updated : o)));
      setSelectedOrder((prev) => (prev && prev._id === orderId ? updated : prev));
      notify('Richiesta modifica rifiutata.');
    } catch (error) {
      console.error('Failed to reject edit request:', error);
      notify('Errore durante il rifiuto della richiesta modifica.', 'err');
    }
    return true;
  };

  const NOTE_BOX = {
    reject: { label: 'Motivo del rifiuto ordine?', cta: 'Rifiuta ordine', run: handleRejectOrder },
    approveEdit: { label: 'Nota interna opzionale per approvazione?', cta: 'Approva richiesta modifica', run: handleApproveEditRequest },
    rejectEdit: { label: 'Motivo rifiuto richiesta modifica?', cta: 'Rifiuta richiesta modifica', run: handleRejectEditRequest },
  };
  const confirmNoteBox = async () => {
    if (!noteBox) return;
    const done = await NOTE_BOX[noteBox.action].run(noteBox.orderId, noteBox.text);
    if (done) setNoteBox(null);
  };

  const handleUserProfileSave = async () => {
    if (!selectedUser) return;
    const parsedDiscount = Number(newDiscount);
    if (!Number.isFinite(parsedDiscount) || parsedDiscount < 0 || parsedDiscount > 100) {
      notify('Inserisci uno sconto valido (0-100)', 'err');
      return;
    }
    if (!newCompanyName || !newCompanyName.trim()) {
      notify('Inserisci un nome azienda', 'err');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        role: newRole,
        discount: parsedDiscount,
        companyName: newCompanyName.trim(),
        country: newCountry === 'OTHER' ? '' : newCountry,
        quoteEnabled: !!newQuoteEnabled,
        phoneNumber: newPhoneNumber,
        address: newAddress,
      };
      if (newPreferredLanguage) {
        payload.preferredLanguage = newPreferredLanguage;
      }

      await userService.updateUserProfile(
        selectedUser._id,
        payload,
        authToken
      );
      notify('Cliente aggiornato');
      const allUsers = await userService.getAllUsers(authToken);
      setUsers(allUsers);
      const refreshedUser = allUsers.find((u) => u._id === selectedUser._id) || null;
      setSelectedUser(refreshedUser);
    } catch (error) {
      console.error('Error updating user profile:', error);
      notify('Errore durante aggiornamento cliente', 'err');
    } finally {
      setLoading(false);
    }
  };
  const handleToggleQuote = async (userToUpdate) => {
    setLoading(true);
    try {
      await userService.updateUserQuoteEnabled(
        userToUpdate._id,
        !userToUpdate.quoteEnabled,
        authToken
      );
      // ricarica la lista utenti aggiornata
      const allUsers = await userService.getAllUsers(authToken);
      setUsers(allUsers);
    } catch (error) {
      console.error('Error updating user quote permission:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (email) => {
    setLoading(true);
    try {
      await userService.sendPasswordResetEmail(email, user.token);
      notify('Email per il reset della password inviata');
    } catch (error) {
      console.error('Error sending password reset email:', error);
    } finally {
      setLoading(false);
    }
  };

  // Conferma nella pagina ("Sei sicuro di voler eliminare questo utente?"), poi come prima.
  const handleDeleteUser = async () => {
    setLoading(true);
    try {
      await userService.deleteUser(selectedUser._id, authToken);
      notify('Utente eliminato con successo');
      setUsers(users.filter((u) => u._id !== selectedUser._id));
      setSelectedUser(null);
      setDrawer(null);
      setConfirmDelete(false);
    } catch (error) {
      console.error('Errore durante l\'eliminazione dell\'utente:', error);
      notify('Errore durante l\'eliminazione dell\'utente', 'err');
    } finally {
      setLoading(false);
    }
  };

  const navigateToCreateUser = () => {
    navigate('/create-user');
  };

  const navigateToProductManager = () => {
    navigate('/product-manager'); // Funzione per navigare a ProductManager
  };

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sevenDaysAgo = new Date(todayStart);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);

  const ordersToday = orders.filter((order) => {
    const orderDate = order?.date ? new Date(order.date) : null;
    return orderDate instanceof Date && !Number.isNaN(orderDate) && orderDate >= todayStart;
  }).length;

  const ordersLast7Days = orders.filter((order) => {
    const orderDate = order?.date ? new Date(order.date) : null;
    return orderDate instanceof Date && !Number.isNaN(orderDate) && orderDate >= sevenDaysAgo;
  });
  const ordersLast7DaysCount = ordersLast7Days.length;
  const ordersLast7DaysValue = ordersLast7Days.reduce((sum, order) => sum + orderAmount(order), 0);
  const preparedOrdersCount = orders.filter((order) => order.status === 'preparato').length;

  const totalOrdersValue = orders.reduce((sum, order) => sum + orderAmount(order), 0);
  const averageOrderValue = orders.length > 0 ? totalOrdersValue / orders.length : 0;
  const onlineUsersCount = users.filter((u) => !!u.isOnline).length;
  const usersNeedsDiscountCount = users.filter((u) => !u.discount || Number(u.discount) === 0).length;
  const usersWithPreparedOrders = users.filter((u) =>
    orders.some((o) => o.userId === u._id && o.status === 'preparato')
  ).length;
  const usersWithSentOrders = users.filter((u) =>
    orders.some((o) => o.userId === u._id && o.status === 'inviato')
  ).length;

  if (!user || user.role !== 'admin') {
    return <div>Loading...</div>;
  }

  const fmtEur = (value) => `€ ${Number(value || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const initials = (name) => String(name || '?').trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  const presenceOf = (u) => (u.isOnline ? 'online' : u.isLogged ? 'logged' : 'offline');
  const PRESENCE_LABEL = { online: 'Online', logged: 'Collegato', offline: 'Offline' };
  const sortMark = (field) => (sortField === field ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : '');
  const sortTh = (field, label) => (
    <th
      className={`is-sort${sortField === field ? ` is-on sorted-${sortOrder}` : ''}`}
      onClick={() => handleSort(field)}
    >
      {label}{sortMark(field)}
    </th>
  );
  const orderStatusLabel = (status) => {
    if (status === 'edit_requested') return 'Richiesta modifica';
    if (status === 'ricevuto') return 'Ricevuto';
    if (status === 'inviato') return 'Inviato';
    return status || '-';
  };
  const newFeedbackCount = feedbackItems.filter((item) => item.status === 'new').length;
  const drawerOpen = !!(drawer && selectedUser);
  const drawerTab = drawer?.tab || 'profile';
  const setDrawerTab = (tab) => {
    setSelectedOrder(null);
    setNoteBox(null);
    setDrawer((prev) => (prev ? { ...prev, tab } : prev));
  };
  const quickFilters = [
    { key: 'all', count: users.length, title: 'Tutti i clienti', hint: 'elenco completo', tone: '' },
    { key: 'needs-discount', count: usersNeedsDiscountCount, title: 'Nuovi senza sconto', hint: 'da sistemare', tone: usersNeedsDiscountCount > 0 ? 'is-warn' : '' },
    {
      key: 'to-prepare',
      count: usersWithPreparedOrders,
      title: 'Ordini da inviare',
      hint: preparedOrdersCount > 0 ? `${preparedOrdersCount} ordini preparati` : 'nessun ordine preparato',
      tone: preparedOrdersCount > 0 ? 'is-warn' : '',
    },
    { key: 'sent-awaiting', count: usersWithSentOrders, title: 'Inviati da verificare', hint: 'da segnare ricevuti', tone: usersWithSentOrders > 0 ? 'is-ok' : '' },
    { key: 'online', count: onlineUsersCount, title: 'Solo online', hint: 'clienti collegati', tone: 'is-info' },
  ];

  return (
    <div className="ad">
      <section className="ad-band">
        <div className="ad-head">
          <div>
            <h1>Pannello <em>·</em> admin</h1>
            <p>Benvenuto, {user.name}</p>
          </div>
          <div className="ad-head-acts">
            {(versionInfo || versionError) && (
              <span className="ad-build" title="Versione del server">
                <i aria-hidden="true" />
                {versionInfo
                  ? <>Build {versionInfo.version} · server attivo dal {new Date(versionInfo.startedAt).toLocaleString()}</>
                  : versionError}
              </span>
            )}
            {loading && <span className="ad-loading" role="status">Caricamento…</span>}
            <button type="button" className="ad-btn" onClick={navigateToProductManager}>
              <FiBox aria-hidden="true" /> Gestione Prodotti
            </button>
            <button type="button" className="ad-btn is-primary" onClick={navigateToCreateUser}>
              <FiPlus aria-hidden="true" /> Nuovo utente
            </button>
          </div>
        </div>

        <div className="ad-kpis">
          <div className="ad-kpi"><span>Ordini oggi</span><b>{ordersToday}</b></div>
          <div className="ad-kpi"><span>Ordini 7 giorni</span><b>{ordersLast7DaysCount}</b></div>
          <div className="ad-kpi"><span>Valore 7 giorni</span><b>{fmtEur(ordersLast7DaysValue)}</b></div>
          <div className="ad-kpi"><span>Valore totale ordini</span><b>{fmtEur(totalOrdersValue)}</b></div>
          <div className="ad-kpi"><span>Ticket medio</span><b>{fmtEur(averageOrderValue)}</b></div>
          <div className="ad-kpi is-live"><span>Utenti online</span><b>{onlineUsersCount}</b></div>
        </div>
      </section>

      <div className="ad-body">

        <section className="ad-panel">
          <div className="ad-tabs is-bar">
            <button type="button" role="tab" aria-selected={!showFeedbackTab} onClick={() => setShowFeedbackTab(false)}>
              Clienti <span className="ad-count">{filteredUsers.length}</span>
            </button>
            <button type="button" role="tab" aria-selected={showFeedbackTab} onClick={() => setShowFeedbackTab(true)}>
              Feedback clienti {newFeedbackCount > 0 && <span className="ad-count is-new">{newFeedbackCount} nuovi</span>}
            </button>
            {!showFeedbackTab ? (
            <div className="ad-tools is-inline">
              <label className="ad-search" htmlFor="search">
                <FiSearch aria-hidden="true" />
                <input
                  type="text"
                  id="search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Cerca utenti per nome o email…"
                  aria-label="Cerca utenti per nome o email"
                />
              </label>
              <select id="roleFilter" className="ad-select" value={roleFilter} onChange={handleRoleFilterChange} aria-label="Filtra per ruolo">
                <option value="">Tutti i ruoli</option>
                <option value="admin">Admin</option>
                <option value="elettricista">Elettricista</option>
                <option value="architetto">Architetto</option>
                <option value="privato">Privato</option>
                <option value="agenzia">Agenzia</option>
                <option value="azienda">Azienda</option>
              </select>
              <span className="ad-tools-l">Ordina</span>
              <select id="sortField" className="ad-select" value={sortField} onChange={(e) => setSortField(e.target.value)} aria-label="Ordina per">
                <option value="name">Nome</option>
                <option value="email">Email</option>
                <option value="role">Ruolo</option>
                {/* le altre colonne si ordinano cliccando l'intestazione */}
                {!['name', 'email', 'role'].includes(sortField) && <option value={sortField}>Colonna</option>}
              </select>
              <select id="sortOrder" className="ad-select" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} aria-label="Ordine">
                <option value="asc">Ascendente</option>
                <option value="desc">Discendente</option>
              </select>
            </div>
            ) : (
            <div className="ad-tools is-inline">
              <label className="ad-tools-l" htmlFor="feedbackStatusFilter">Filtro stato</label>
              <select
                id="feedbackStatusFilter"
                className="ad-select"
                value={feedbackStatusFilter}
                onChange={(e) => setFeedbackStatusFilter(e.target.value)}
              >
                <option value="">Tutti</option>
                <option value="new">Nuovo</option>
                <option value="in_review">In lavorazione</option>
                <option value="closed">Chiuso</option>
              </select>
            </div>
            )}
          </div>

          {!showFeedbackTab ? (
            <>
              <div className="ad-qf" role="group" aria-label="Filtri rapidi">
                {quickFilters.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={`ad-qf-b ${f.tone}`}
                    aria-pressed={actionFilter === f.key}
                    title={f.hint}
                    onClick={() => { setActionFilter(f.key); setCurrentPage(1); }}
                  >
                    {f.title} <span className="ad-qf-n">{f.count}</span>
                    {f.key === 'to-prepare' && preparedOrdersCount > 0 && <small>· {preparedOrdersCount} ordini</small>}
                  </button>
                ))}
              </div>
              <div className="ad-tablewrap">
                <table className="ad-table">
                  <thead>
                    <tr>
                      <th className="is-num">#</th>
                      {sortTh('name', 'Cliente')}
                      {sortTh('companyName', 'Azienda')}
                      {sortTh('country', 'Paese')}
                      {sortTh('role', 'Ruolo')}
                      {sortTh('createdAt', 'Registrato')}
                      {sortTh('lastAccess', 'Ultimo accesso')}
                      <th>Sconto</th>
                      <th className="is-quote">Preventivi</th>
                      <th>
                        Ordini
                        {orders.some((order) => order.status === 'inviato') && <i className="ad-blink-dot" title="Ci sono ordini inviati" />}
                      </th>
                      <th aria-label="Azioni" />
                    </tr>
                  </thead>
                  <tbody>
                    {currentUsers && currentUsers.length > 0 ? (
                      currentUsers.map((rowUser, index) => {
                        const userSpecificOrders = orders.filter((order) => order.userId === rowUser._id);
                        const preparatoCount = userSpecificOrders.filter((order) => order.status === 'preparato').length;
                        const inviatoCount = userSpecificOrders.filter((order) => order.status === 'inviato').length;
                        const ricevutoCount = userSpecificOrders.filter((order) => order.status === 'ricevuto').length;
                        const registeredAt = rowUser.createdAt ? new Date(rowUser.createdAt).toLocaleDateString() : '-';
                        const lastAccessAt = formatDateTime(rowUser.lastAccess);
                        const needsDiscount = !rowUser.discount || Number(rowUser.discount) === 0;
                        const presence = presenceOf(rowUser);
                        return (
                          <tr key={rowUser._id} className={needsDiscount ? 'is-flag' : ''}>
                            <td className="is-num ad-mono">{(currentPage - 1) * usersPerPage + index + 1}</td>
                            <td className="ad-c-who">
                              <div className="ad-who">
                                <span className={`ad-av is-${presence}`} title={PRESENCE_LABEL[presence]}>{initials(rowUser.name)}<i /></span>
                                <div className="ad-who-t" title={`${rowUser.name} · ${rowUser.email}`}>
                                  <b>{rowUser.name}</b>
                                  <small>{rowUser.email}</small>
                                </div>
                              </div>
                            </td>
                            <td className="ad-c-sm">{rowUser.companyName || '-'}</td>
                            <td className="ad-c-sm ad-mono">{rowUser.country || '-'}</td>
                            <td><span className={`ad-role is-${rowUser.role}`}>{rowUser.role}</span></td>
                            <td className="ad-c-sm ad-mono"><span className="ad-ph-l">Registrato </span>{registeredAt}</td>
                            <td className="ad-c-sm ad-mono"><span className="ad-ph-l">Ultimo accesso </span>{lastAccessAt}</td>
                            <td>
                              {needsDiscount
                                ? <span className="ad-need" title="Nuovo utente senza sconto assegnato"><span>{rowUser.discount ?? 0}% · </span>da assegnare</span>
                                : <span className="ad-mono ad-disc">{rowUser.discount}%</span>}
                            </td>
                            <td className="is-quote">
                              <button
                                type="button"
                                className="ad-switch"
                                role="switch"
                                aria-checked={!!rowUser.quoteEnabled}
                                onClick={() => handleToggleQuote(rowUser)}
                                title="Abilita funzione preventivi"
                                aria-label={`Preventivi ${rowUser.name}`}
                              />
                            </td>
                            <td>
                              <span className="ad-oc" title="Da inviare / Inviati / Ricevuti">
                                <span className={`is-prep${preparatoCount ? ' has' : ''}`}>{preparatoCount}</span>
                                <span className={`is-sent${inviatoCount ? ' has' : ''}`}>{inviatoCount}</span>
                                <span className={`is-recv${ricevutoCount ? ' has' : ''}`}>{ricevutoCount}</span>
                              </span>
                            </td>
                            <td>
                              <div className="ad-rowacts">
                                <button type="button" className="ad-btn is-sm" onClick={() => openUserDrawer(rowUser, 'profile')} title="Manage User">
                                  <FiUser aria-hidden="true" /><span className="ad-btn-t">Scheda</span>
                                </button>
                                <button type="button" className="ad-btn is-sm" onClick={() => openUserDrawer(rowUser, 'orders')} title="Manage Order">
                                  <FiClipboard aria-hidden="true" /><span className="ad-btn-t">Ordini</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="11" className="ad-empty">No users found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="ad-foot">
                <div className="ad-legend">
                  <span><i className="is-online" />Online</span>
                  <span><i className="is-logged" />Collegato</span>
                  <span><i className="is-offline" />Offline</span>
                  <span>Ordini: <b className="is-prep">da inviare</b> / <b className="is-sent">inviati</b> / ricevuti</span>
                </div>
                <div className="ad-pages">
                  <Pagination usersPerPage={usersPerPage} totalUsers={filteredUsers.length} paginate={setCurrentPage} />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="ad-fb">
                {feedbackLoading ? (
                  <p className="ad-empty">Caricamento feedback...</p>
                ) : feedbackItems.length === 0 ? (
                  <p className="ad-empty">Nessun feedback trovato.</p>
                ) : (
                  feedbackItems.map((item) => (
                    <article key={item._id} className={`ad-fbi${item.status === 'new' || !item.status ? ' is-new' : ''}`}>
                      <div className="ad-fbi-meta">
                        <span className={`ad-tag is-${item.type}`}>{feedbackTypeLabel(item.type)}</span>
                        <span>Priorità: {item.priority || '-'}</span>
                        <b>{item.userName || item.userEmail || '-'}</b>
                        <span>{item.companyName || '-'}</span>
                        <span className="ad-mono">{formatDateTime(item.createdAt)}</span>
                      </div>
                      <p>{item.message}</p>
                      <div className="ad-fbi-st">
                        <select
                          className="ad-select"
                          value={item.status || 'new'}
                          onChange={(e) => handleFeedbackStatusChange(item._id, e.target.value)}
                          aria-label="Stato feedback"
                        >
                          <option value="new">Nuovo</option>
                          <option value="in_review">In lavorazione</option>
                          <option value="closed">Chiuso</option>
                        </select>
                        <small>{feedbackStatusLabel(item.status)}</small>
                      </div>
                    </article>
                  ))
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {drawerOpen && (
        <div className="ad-drawer" onKeyDown={(e) => { if (e.key === 'Escape') handleBack(); }}>
          <div className="ad-drawer-bg" onClick={handleBack} aria-hidden="true" />
          <aside className="ad-dw" aria-label="Scheda cliente">
            <div className="ad-dh">
              <span className={`ad-av is-lg is-${presenceOf(selectedUser)}`}>{initials(selectedUser.name)}<i /></span>
              <div className="ad-dh-t">
                <h2>{selectedUser.name || '-'}</h2>
                <small>{selectedUser.email || '-'}{selectedUser.companyName ? ` · ${selectedUser.companyName}` : ''}</small>
              </div>
              <button type="button" className="ad-btn is-sm" onClick={handleBack}>
                <FiX aria-hidden="true" /> Chiudi
              </button>
            </div>
            <div className="ad-tabs is-drawer" role="tablist">
              <button type="button" role="tab" aria-selected={drawerTab === 'profile'} onClick={() => setDrawerTab('profile')}>Profilo e condizioni</button>
              <button type="button" role="tab" aria-selected={drawerTab === 'orders'} onClick={() => setDrawerTab('orders')}>
                Ordini <span className="ad-count">{userOrders.length}</span>
              </button>
            </div>

            <div className="ad-db">
              {drawerTab === 'profile' ? (
                <>
                  <div className="ad-card">
                    <h3>Account</h3>
                    <div className="ad-kv">
                      <span>Nome</span><b>{selectedUser.name || '-'}</b>
                      <span>Email</span><b>{selectedUser.email || '-'}</b>
                      <span>Ultimo accesso</span><b className="ad-mono">{formatDateTime(selectedUser.lastAccess)}</b>
                    </div>
                  </div>

                  <div className="ad-card">
                    <h3>Condizioni commerciali</h3>
                    <div className="ad-fg">
                      <div className="ad-f">
                        <label htmlFor="ad-role">Ruolo</label>
                        <select id="ad-role" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                          <option value="elettricista">Elettricista</option>
                          <option value="architetto">Architetto</option>
                          <option value="privato">Privato</option>
                          <option value="agenzia">Agenzia</option>
                          <option value="azienda">Azienda</option>
                        </select>
                      </div>
                      <div className="ad-f">
                        <label htmlFor="ad-discount">Sconto (%)</label>
                        <div className="ad-disc-in">
                          <input
                            id="ad-discount"
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            value={newDiscount}
                            onChange={(e) => setNewDiscount(e.target.value)}
                            placeholder="0-100"
                          />
                          <div className="ad-chips" aria-label="Sconti rapidi">
                            {[30, 35, 40, 45, 50].map((d) => (
                              <button key={d} type="button" onClick={() => setNewDiscount(d)} aria-pressed={Number(newDiscount) === d}>{d}</button>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="ad-f is-full">
                        <span className="ad-f-l">Preventivi</span>
                        <label className="ad-check">
                          <input
                            type="checkbox"
                            checked={!!newQuoteEnabled}
                            onChange={(e) => setNewQuoteEnabled(e.target.checked)}
                          />
                          Abilitato <small>il cliente può preparare preventivi per i suoi clienti</small>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="ad-card">
                    <h3>Azienda e contatti</h3>
                    <div className="ad-fg">
                      <div className="ad-f is-full">
                        <label htmlFor="ad-company">Azienda</label>
                        <input
                          id="ad-company"
                          type="text"
                          value={newCompanyName}
                          onChange={(e) => setNewCompanyName(e.target.value)}
                          placeholder="Inserisci nome azienda"
                        />
                        <span className="ad-help">Chiave attuale: {selectedUser.companyKey || '-'}</span>
                      </div>
                      <div className="ad-f">
                        <label htmlFor="ad-country">Paese</label>
                        <select id="ad-country" value={newCountry || ''} onChange={(e) => setNewCountry(e.target.value)}>
                          {COUNTRY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="ad-f">
                        <label htmlFor="ad-lang">Lingua</label>
                        <select id="ad-lang" value={newPreferredLanguage} onChange={(e) => setNewPreferredLanguage(e.target.value)}>
                          {LANGUAGE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="ad-f">
                        <label htmlFor="ad-phone">Telefono</label>
                        <input id="ad-phone" type="text" value={newPhoneNumber} onChange={(e) => setNewPhoneNumber(e.target.value)} placeholder="Telefono cliente" />
                      </div>
                      <div className="ad-f">
                        <label htmlFor="ad-address">Indirizzo</label>
                        <input id="ad-address" type="text" value={newAddress} onChange={(e) => setNewAddress(e.target.value)} placeholder="Indirizzo cliente" />
                      </div>
                    </div>
                  </div>

                  <div className="ad-card is-danger">
                    <h3>Zona attenzione</h3>
                    <div className="ad-dz-r">
                      <span>Invia al cliente l'email per reimpostare la password</span>
                      <button type="button" className="ad-btn is-sm" onClick={() => handlePasswordReset(selectedUser.email)}>Reset Password</button>
                    </div>
                    <div className="ad-dz-r">
                      <span>Elimina definitivamente questo utente</span>
                      <button type="button" className="ad-btn is-sm is-danger" onClick={() => setConfirmDelete(true)}>Delete User</button>
                    </div>
                    {confirmDelete && (
                      <div className="ad-confirm" role="alertdialog" aria-label="Conferma eliminazione">
                        <span>Sei sicuro di voler eliminare questo utente? <b>{selectedUser.name}</b></span>
                        <button type="button" className="ad-btn is-sm is-danger is-solid" onClick={handleDeleteUser} disabled={loading}>Sì, elimina</button>
                        <button type="button" className="ad-btn is-sm" onClick={() => setConfirmDelete(false)}>Annulla</button>
                      </div>
                    )}
                  </div>

                  <div className="ad-savebar">
                    <small>Le modifiche si applicano dopo il salvataggio</small>
                    <button type="button" className="ad-btn is-primary" onClick={handleUserProfileSave} disabled={loading}>
                      {loading ? 'Salvataggio...' : 'Salva aggiornamento cliente'}
                    </button>
                  </div>
                </>
              ) : !selectedOrder ? (
                <div className="ad-olist">
                  {userOrders.length > 0 ? (
                    userOrders.map((order) => (
                      <button
                        key={order._id}
                        type="button"
                        className={`ad-oi${order.status === 'ricevuto' ? ' is-dim' : ''}`}
                        onClick={() => handleViewOrderDetails(order._id)}
                      >
                        <span className="ad-oi-t">
                          <b>Ordine #{order.orderNumber}</b> · {order.reference}
                          {order.date && <small>{new Date(order.date).toLocaleDateString()}</small>}
                        </span>
                        <span className="ad-oi-r">
                          <span className={`ad-st is-${order.status}`}>
                            {order.status === 'ricevuto' && <FiCheck aria-hidden="true" />} {orderStatusLabel(order.status)}
                          </span>
                          <span className="ad-mono">{fmtEur(orderAmount(order))}</span>
                        </span>
                      </button>
                    ))
                  ) : (
                    <p className="ad-empty">Nessun ordine trovato per questo utente.</p>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <button type="button" className="ad-btn is-sm" onClick={() => { setSelectedOrder(null); setNoteBox(null); }}>
                      <FiArrowLeft aria-hidden="true" /> Back to Orders
                    </button>
                  </div>
                  <div className="ad-card ad-od">
                    <div className="ad-od-h">
                      <div>
                        <h3>Dettagli Ordine #{selectedOrder.orderNumber}</h3>
                        <b>Riferimento: {selectedOrder.reference}</b>
                        <small>Utente: {users.find((u) => u._id === selectedOrder.userId)?.email || 'N/A'}</small>
                      </div>
                      <span className={`ad-st is-${selectedOrder.status}`}>{orderStatusLabel(selectedOrder.status)}</span>
                    </div>
                    <div className="ad-tablewrap">
                      <table className="ad-otable">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Codice</th>
                            <th>Descrizione</th>
                            <th className="is-n">Prezzo Unitario</th>
                            <th className="is-n">Quantità</th>
                            <th className="is-n">Prezzo Totale</th>
                            <th>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.items && selectedOrder.items.length > 0 ? (
                            selectedOrder.items.map((item, index) => (
                              <tr key={index}>
                                <td className="ad-mono">{index + 1}</td>
                                <td className="ad-mono">{item.code}</td>
                                <td>{item.description}</td>
                                <td className="is-n">{fmtEur(parseMoneyValue(item.unitPrice))}</td>
                                <td className="is-n">{item.quantity}</td>
                                <td className="is-n">{fmtEur(parseMoneyValue(item.unitPrice) * (Number(item.quantity) || 0))}</td>
                                <td className="ad-note">{item.notes || ''}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="ad-empty">Nessun articolo trovato per questo ordine.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="ad-od-tot"><span>Totale</span><b>EUR {selectedOrder.total}</b></div>
                    <div className="ad-oacts">
                      <button type="button" className="ad-btn" onClick={() => exportOrderToPDF(selectedOrder)}>
                        <FiPrinter aria-hidden="true" /> Stampa Ordine
                      </button>
                      <button type="button" className="ad-btn is-ok" onClick={() => handleMarkAsReceived(selectedOrder._id)}>
                        <FiCheck aria-hidden="true" /> Segna come ricevuto
                      </button>
                      <button type="button" className="ad-btn is-danger" onClick={() => setNoteBox({ action: 'reject', orderId: selectedOrder._id, text: '' })}>
                        Rifiuta Ordine…
                      </button>
                      {selectedOrder.status === 'edit_requested' && selectedOrder.editRequest?.status === 'pending' ? (
                        <>
                          <button type="button" className="ad-btn is-primary" onClick={() => setNoteBox({ action: 'approveEdit', orderId: selectedOrder._id, text: '' })}>
                            Approva Richiesta Modifica…
                          </button>
                          <button type="button" className="ad-btn" onClick={() => setNoteBox({ action: 'rejectEdit', orderId: selectedOrder._id, text: '' })}>
                            Rifiuta Richiesta Modifica…
                          </button>
                        </>
                      ) : null}
                    </div>
                    {noteBox && (
                      <div className="ad-notebox">
                        <label htmlFor="ad-note">{NOTE_BOX[noteBox.action].label}</label>
                        <textarea
                          id="ad-note"
                          autoFocus
                          value={noteBox.text}
                          onChange={(e) => setNoteBox((prev) => ({ ...prev, text: e.target.value }))}
                        />
                        <div className="ad-oacts">
                          <button
                            type="button"
                            className={`ad-btn is-sm ${noteBox.action === 'reject' ? 'is-danger is-solid' : 'is-primary'}`}
                            onClick={confirmNoteBox}
                          >
                            {NOTE_BOX[noteBox.action].cta}
                          </button>
                          <button type="button" className="ad-btn is-sm" onClick={() => setNoteBox(null)}>Annulla</button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </aside>
        </div>
      )}

      {notice && (
        <div key={notice.at} className={`ad-toast${notice.kind === 'err' ? ' is-err' : ''}${notice.sticky ? ' is-sticky' : ''}`} role="status">
          <span>{notice.text}</span>
          {notice.sticky && (
            <button type="button" className="ad-toast-x" onClick={() => setNotice(null)} aria-label="Chiudi messaggio">✕</button>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
